import * as THREE from "three";
import type { GroundPoint } from "./dugArea";
import { tintedSurfaceShare } from "./surfaceTextures";
import type { ScatterArea, ScatterContribution, ScatterKind } from "./surfaceScatterSampling";
import { ScatterComputer } from "./surfaceScatterCompute";
import type { ScatterFrame, ScatterRequest, ScatterResponse, ScatterResult } from "./surfaceScatterCompute";
import type { RoadFeature } from "./roadClearings";

/** Les éléments rapetissent d'eux-mêmes au loin (voir `SCATTER_VERTEX_BODY`) : on ne les retire qu'une fois presque rentrés dans le sol. */
const HIDDEN_BELOW_PX = 1.5;
const FULL_ABOVE_PX = 3;

interface KindLook {
  size: number;
  geometry: () => THREE.BufferGeometry;
  side: THREE.Side;
  /** Herbe : dégradée du pied à la pointe et agitée par le vent ; le reste est ombré selon l'orientation de ses faces. */
  grass?: boolean;
}

const GRASS_BLADES = 7;

function grassClump(): THREE.BufferGeometry {
  const rand = (() => {
    let state = 7;
    return () => ((state = (state * 16807) % 2147483647) - 1) / 2147483646;
  })();
  const positions: number[] = [];
  const sides: number[] = [];
  const heights: number[] = [];
  const indices: number[] = [];
  for (let blade = 0; blade < GRASS_BLADES; blade++) {
    const angle = (blade / GRASS_BLADES) * Math.PI * 2 + rand() * 0.6;
    const spread = 0.03 + rand() * 0.11;
    const [ox, oz] = [Math.cos(angle) * spread, Math.sin(angle) * spread];
    const height = 0.3 + rand() * 0.18;
    const lean = 0.06 + rand() * 0.1;
    const [lx, lz] = [Math.cos(angle) * lean, Math.sin(angle) * lean];
    const facing = angle + Math.PI / 2 + (rand() - 0.5) * 0.8;
    const [wx, wz] = [Math.cos(facing), Math.sin(facing)];
    const at = (ratio: number, width: number) => {
      const bend = ratio * ratio;
      const x = ox + lx * bend;
      const z = oz + lz * bend;
      positions.push(x - wx * width, ratio * height, z - wz * width, x + wx * width, ratio * height, z + wz * width);
      sides.push(-wx * width, 0, -wz * width, wx * width, 0, wz * width);
      heights.push(ratio, ratio);
    };
    const first = positions.length / 3;
    at(0, 0.028);
    at(0.55, 0.019);
    positions.push(ox + lx, height, oz + lz);
    sides.push(0, 0, 0);
    heights.push(1);
    indices.push(first, first + 1, first + 2, first + 1, first + 3, first + 2, first + 2, first + 3, first + 4);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(positions.map((_, index) => (index % 3 === 1 ? 1 : 0)), 3));
  geometry.setAttribute("grassHeight", new THREE.Float32BufferAttribute(heights, 1));
  geometry.setAttribute("grassSide", new THREE.Float32BufferAttribute(sides, 3));
  geometry.setIndex(indices);
  return geometry;
}

export interface ScatterView {
  /** Profondeur, dans la projection de la caméra, du centre de la carte au sol. */
  centerDepth: number;
  metersPerPixel: number;
  zoom: number;
  /** Secondes ; n'avance que quand les animations jouent. */
  time: number;
}

const SCATTER_VERTEX_HEAD = /* glsl */ `
  uniform float uTime;
  uniform float uCenterDepth;
  uniform float uMetersPerPixel;
  uniform float uSize;
  varying float vScatterShade;
  #ifdef SCATTER_GRASS
    attribute float grassHeight;
    attribute vec3 grassSide;
    varying float vGust;
  #endif
`;

// Au loin, un élément rentre dans le sol à mesure qu'il rapetisse à l'écran, au lieu de disparaître d'un coup.
const SCATTER_VERTEX_BODY = /* glsl */ `
  vec3 transformed = vec3(position);
  vec4 scatterOrigin = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  vec4 scatterClip = projectionMatrix * modelViewMatrix * scatterOrigin;
  float scatterPixelsPerMeter = scatterClip.w > 0.0 ? uCenterDepth / (scatterClip.w * uMetersPerPixel) : 0.0;
  float scatterFade = smoothstep(1.5, 6.0, uSize * scatterPixelsPerMeter);
  mat3 scatterRotation = mat3(instanceMatrix);
  #ifdef SCATTER_GRASS
    transformed.y *= scatterFade;
    // Un brin plus fin qu'un pixel à l'écran scintille au lieu d'onduler : il s'élargit pour ne jamais passer dessous.
    float bladePixels = 2.0 * length(grassSide) * length(scatterRotation[0]) * scatterPixelsPerMeter;
    transformed += grassSide * (clamp(1.1 / max(bladePixels, 0.0001), 1.0, 3.0) - 1.0);
    float gust = 0.5 + 0.5 * sin(uTime * 0.45 - dot(scatterOrigin.xz, vec2(0.06, 0.04)));
    float wave = sin(uTime * 1.4 - dot(scatterOrigin.xz, vec2(0.24, 0.16)));
    float sway = wave * (0.3 + 0.7 * gust) + 0.18 * sin(uTime * 3.3 + scatterOrigin.x * 1.3 - scatterOrigin.z * 0.7);
    vec3 wind = vec3(0.16, 0.0, 0.1) * sway * grassHeight * grassHeight * scatterFade;
    transformed += transpose(scatterRotation) * wind / dot(scatterRotation[0], scatterRotation[0]);
    vScatterShade = grassHeight;
    vGust = max(0.0, wave) * gust;
  #else
    transformed *= scatterFade;
    vScatterShade = clamp(normalize(scatterRotation * normal).y, 0.0, 1.0);
  #endif
  if (scatterClip.w <= 0.0) transformed = vec3(0.0);
`;

const SCATTER_FRAGMENT_HEAD = /* glsl */ `
  varying float vScatterShade;
  #ifdef SCATTER_GRASS
    varying float vGust;
  #endif
`;

const SCATTER_FRAGMENT_COLOR = /* glsl */ `
  #include <color_fragment>
  #ifdef SCATTER_GRASS
    diffuseColor.rgb *= mix(0.68, 1.3, vScatterShade) * (1.0 + 0.16 * vGust * vScatterShade);
    diffuseColor.rgb += vec3(0.1, 0.085, 0.0) * vScatterShade * vScatterShade;
  #else
    diffuseColor.rgb *= mix(0.7, 1.08, vScatterShade);
  #endif
`;

const LOOKS: Record<ScatterKind, KindLook> = {
  tufts: {
    size: 0.4,
    side: THREE.DoubleSide,
    grass: true,
    geometry: grassClump,
  },
  dryTufts: {
    size: 0.3,
    side: THREE.DoubleSide,
    grass: true,
    geometry: grassClump,
  },
  flowers: {
    size: 0.25,
    side: THREE.FrontSide,
    geometry: () => new THREE.OctahedronGeometry(0.045).translate(0, 0.2, 0),
  },
  stones: {
    size: 0.15,
    side: THREE.FrontSide,
    geometry: () => new THREE.IcosahedronGeometry(0.08, 0).scale(1, 0.55, 0.85).translate(0, 0.03, 0),
  },
  leaves: {
    size: 0.12,
    side: THREE.DoubleSide,
    geometry: () => new THREE.PlaneGeometry(0.11, 0.06).rotateX(-Math.PI / 2).translate(0, 0.01, 0),
  },
};

/**
 * Éléments semés dessinés au plus par image, toutes zones confondues : une vue d'ensemble penchée sur
 * des bois denses en demandait plusieurs centaines de milliers, soit des millions de triangles.
 */
export const SCATTER_BUDGET = 60_000;
/** Plus grand agrandissement d'un élément semé (voir `scale` dans `surfaceScatterSampling`), de quoi borner un carreau sans relire chaque élément. */
const SCATTER_MAX_SCALE = 3;
/** Temps accordé par tâche à la reconstruction des carreaux (voir `rebuild`). */
const SCATTER_REBUILD_BUDGET_MS = 4;

/**
 * Ramène les comptes voulus par carreau (`wanted`) sous `budget` en éclaircissant d'abord les plus
 * lointains : un carreau garde la part `min(1, f × scale)` de ses éléments, `scale` étant sa taille
 * à l'écran, et `f` est le plus grand facteur qui tient dans le budget. De près, rien ne change.
 */
export function scatterBudgetCounts(wanted: number[], scales: number[], budget: number): number[] {
  const total = wanted.reduce((sum, value) => sum + value, 0);
  if (total <= budget) return wanted;
  const kept = (factor: number) => wanted.reduce((sum, value, index) => sum + value * Math.min(1, factor * scales[index]!), 0);
  let low = 0;
  let high = 1 / Math.max(1e-9, Math.min(...scales.filter((scale) => scale > 0)));
  for (let step = 0; step < 30; step++) {
    const middle = (low + high) / 2;
    if (kept(middle) > budget) high = middle;
    else low = middle;
  }
  return wanted.map((value, index) => Math.floor(value * Math.min(1, low * scales[index]!)));
}

/** La part des éléments d'un carreau qui reste dessinée, selon leur taille à l'écran. */
export function scatterShare(sizePx: number): number {
  return Math.max(0, Math.min(1, (sizePx - HIDDEN_BELOW_PX) / (FULL_ABOVE_PX - HIDDEN_BELOW_PX)));
}

/** Les contributions d'un même carreau fondues en une seule, toujours triées par rang. */
export function mergeContributions(parts: ScatterContribution[]): Pick<ScatterContribution, "matrices" | "colors"> {
  if (parts.length === 1) return parts[0]!;
  const total = parts.reduce((sum, part) => sum + part.ranks.length, 0);
  const matrices = new Float32Array(total * 16);
  const colors = new Float32Array(total * 3);
  // Fusion des contributions, chacune déjà triée par rang : on recopie d'un bloc chaque suite qui passe avant toutes les autres.
  const cursors = parts.map(() => 0);
  let target = 0;
  while (target < total) {
    let first = -1;
    for (let index = 0; index < parts.length; index++) {
      if (cursors[index]! < parts[index]!.ranks.length && (first < 0 || parts[index]!.ranks[cursors[index]!]! < parts[first]!.ranks[cursors[first]!]!)) first = index;
    }
    // À rang égal, la contribution listée la première passe devant, comme le ferait un tri stable.
    let before = Infinity;
    let after = Infinity;
    for (let index = 0; index < parts.length; index++) {
      if (index === first || cursors[index]! >= parts[index]!.ranks.length) continue;
      const rank = parts[index]!.ranks[cursors[index]!]!;
      if (index < first) before = Math.min(before, rank);
      else after = Math.min(after, rank);
    }
    const part = parts[first]!;
    const start = cursors[first]!;
    let end = start + 1;
    while (end < part.ranks.length && part.ranks[end]! < before && part.ranks[end]! <= after) end++;
    matrices.set(part.matrices.subarray(start * 16, end * 16), target * 16);
    colors.set(part.colors.subarray(start * 3, end * 3), target * 3);
    target += end - start;
    cursors[first] = end;
  }
  return { matrices, colors };
}

/** La sphère qui contient tous les éléments d'un carreau, à `margin` près : lue sur leurs seules positions. */
export function scatterBoundingSphere(matrices: Float32Array, margin: number): THREE.Sphere {
  const box = new THREE.Box3();
  const point = new THREE.Vector3();
  for (let offset = 12; offset < matrices.length; offset += 16) box.expandByPoint(point.set(matrices[offset]!, matrices[offset + 1]!, matrices[offset + 2]!));
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  sphere.radius += margin;
  return sphere;
}

function areaKey(area: ScatterArea, openingsKey: string): string {
  return `${JSON.stringify(area)}|${area.base === 0 ? openingsKey : ""}`;
}

interface ScatterChunk {
  mesh: THREE.InstancedMesh;
  center: THREE.Vector3;
  size: number;
  total: number;
  nearOnly: boolean;
}

export class SurfaceScatter {
  readonly root = new THREE.Group();
  private readonly results = new Map<string, ScatterContribution[]>();
  private shown = new Set<string>();
  private readonly chunks = new Map<string, ScatterChunk>();
  private readonly geometries = new Map<ScatterKind, THREE.BufferGeometry>();
  private readonly materials = new Map<ScatterKind, THREE.Material>();
  private generation = 0;
  private requests = 0;
  private readonly computing = new Set<string>();
  private readonly pendingChunks = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Les zones affichées et les ouvertures de leur dernier semis : un changement de routes les refait. */
  private shownAreas = new Map<string, ScatterArea>();
  private openings: GroundPoint[][] = [];
  /** Éléments dessinés à la dernière image, pour la fenêtre de diagnostic. */
  drawn = 0;
  private worker: Promise<Worker | null> | null = null;
  /** Sans worker (tests, navigateur trop ancien), le même calcul sur le fil principal. */
  private direct: ScatterComputer | null = null;
  private readonly sharedUniforms = { uTime: { value: 0 }, uCenterDepth: { value: 1 }, uMetersPerPixel: { value: 1 } };

  constructor(private readonly onChange: () => void) {
    this.root.name = "SurfaceScatter";
  }

  /** `openings` : les zones creusées, où rien n'est semé au niveau du sol. Le calcul part dans un worker ; seules les formes changées sont recalculées. */
  update(areas: ScatterArea[], openings: GroundPoint[][]): void {
    const openingsKey = JSON.stringify(openings);
    const wanted = new Map(areas.map((area) => [areaKey(area, openingsKey), area]));
    // Une zone déjà en calcul pour une mise à jour précédente n'est pas redemandée : elle arrivera avec elle.
    const missing = [...wanted].filter(([key]) => !this.results.has(key) && !this.computing.has(key)).map(([key, area]) => ({ key, area }));
    for (const { key } of missing) this.computing.add(key);
    const generation = ++this.generation;
    const apply = (computed: ScatterResult[]) => {
      for (const { key, contributions } of computed) {
        this.computing.delete(key);
        this.results.set(key, contributions);
      }
      if (generation !== this.generation) {
        this.applyLate(computed);
        return;
      }
      const affected = new Set<string>();
      for (const key of this.shown) {
        if (!wanted.has(key)) for (const { chunk } of this.results.get(key) ?? []) affected.add(chunk);
      }
      for (const key of wanted.keys()) {
        if (!this.shown.has(key)) for (const { chunk } of this.results.get(key) ?? []) affected.add(chunk);
      }
      this.shown = new Set(wanted.keys());
      this.shownAreas = wanted;
      this.openings = openings;
      for (const key of [...this.results.keys()]) if (!this.shown.has(key)) this.results.delete(key);
      this.rebuild(affected);
      this.onChange();
    };
    if (!missing.length) {
      apply([]);
      return;
    }
    void this.send({ id: 0, type: "compute", areas: missing, openings }).then(apply);
  }

  /**
   * Les routes au-dessus des formes, en longitude/latitude : rien n'est semé dessus. Seules les zones
   * dont les routes proches changent sont semées à nouveau, hors du fil principal.
   */
  setRoads(roads: RoadFeature[] | null, frame: ScatterFrame): void {
    const areas = [...this.shownAreas].filter(([, area]) => area.roadBounds).map(([key, area]) => ({ key, area }));
    void this.send({ id: 0, type: "roads", roads, frame, areas, openings: this.openings }).then((computed) => this.applyLate(computed));
  }

  /** Des zones semées à nouveau après coup : seuls leurs carreaux sont refaits, si elles sont encore affichées. */
  private applyLate(computed: ScatterResult[]): void {
    const affected = new Set<string>();
    for (const { key, contributions } of computed) {
      if (!this.shown.has(key)) continue;
      for (const { chunk } of [...(this.results.get(key) ?? []), ...contributions]) affected.add(chunk);
      this.results.set(key, contributions);
    }
    if (!affected.size) return;
    this.rebuild(affected);
    this.onChange();
  }

  /** `pixelsPerMeterAt` rend l'échelle à l'écran au point donné de la scène. */
  updateDetail(view: ScatterView, pixelsPerMeterAt: (point: THREE.Vector3) => number): void {
    this.sharedUniforms.uTime.value = view.time;
    this.sharedUniforms.uCenterDepth.value = view.centerDepth;
    this.sharedUniforms.uMetersPerPixel.value = view.metersPerPixel;
    const chunks = [...this.chunks.values()];
    const scales = chunks.map((chunk) => pixelsPerMeterAt(chunk.center));
    const wanted = chunks.map((chunk, index) => {
      const share = scatterShare(chunk.size * scales[index]!) * (chunk.nearOnly ? tintedSurfaceShare(view.zoom) : 1);
      return Math.round(chunk.total * share);
    });
    const counts = scatterBudgetCounts(wanted, scales, SCATTER_BUDGET);
    this.drawn = counts.reduce((sum, value) => sum + value, 0);
    chunks.forEach((chunk, index) => {
      chunk.mesh.count = counts[index]!;
      chunk.mesh.visible = counts[index]! > 0;
    });
  }

  get hasContent(): boolean {
    return this.chunks.size > 0;
  }

  dispose(): void {
    this.generation++;
    if (this.flushTimer !== null) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.pendingChunks.clear();
    for (const chunk of this.chunks.values()) chunk.mesh.dispose();
    this.chunks.clear();
    for (const geometry of this.geometries.values()) geometry.dispose();
    for (const material of this.materials.values()) material.dispose();
    void this.worker?.then((worker) => worker?.terminate());
    this.worker = null;
  }

  /**
   * Refait ces carreaux, quelques millisecondes par tâche : tous d'un coup, c'était une centaine de
   * millisecondes figées quand des routes arrivaient. Chaque carreau est remplacé d'un seul tenant.
   */
  private rebuild(affected: Set<string>): void {
    for (const key of affected) this.pendingChunks.add(key);
    if (this.flushTimer === null) this.flushChunks();
  }

  private flushChunks(): void {
    this.flushTimer = null;
    const deadline = performance.now() + SCATTER_REBUILD_BUDGET_MS;
    const partsByChunk = new Map<string, ScatterContribution[]>();
    for (const shownKey of this.shown) {
      for (const contribution of this.results.get(shownKey) ?? []) {
        if (!this.pendingChunks.has(contribution.chunk)) continue;
        let parts = partsByChunk.get(contribution.chunk);
        if (!parts) partsByChunk.set(contribution.chunk, (parts = []));
        parts.push(contribution);
      }
    }
    for (const key of [...this.pendingChunks]) {
      this.pendingChunks.delete(key);
      this.rebuildChunk(key, partsByChunk.get(key));
      if (performance.now() >= deadline) break;
    }
    if (!this.pendingChunks.size) return;
    this.flushTimer = setTimeout(() => {
      this.flushChunks();
      this.onChange();
    }, 0);
  }

  private rebuildChunk(key: string, parts: ScatterContribution[] | undefined): void {
    const previous = this.chunks.get(key);
    if (previous) {
      this.root.remove(previous.mesh);
      previous.mesh.dispose();
      this.chunks.delete(key);
    }
    if (!parts) return;
    const kind = parts[0]!.kind;
    const { matrices, colors } = mergeContributions(parts);
    const total = matrices.length / 16;
    const mesh = new THREE.InstancedMesh(this.geometryOf(kind), this.materialOf(kind), 0);
    mesh.instanceMatrix = new THREE.InstancedBufferAttribute(matrices, 16);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);
    mesh.count = total;
    const geometry = this.geometryOf(kind);
    if (!geometry.boundingSphere) geometry.computeBoundingSphere();
    mesh.boundingSphere = scatterBoundingSphere(matrices, geometry.boundingSphere!.radius * SCATTER_MAX_SCALE);
    mesh.name = `Scatter:${key}`;
    this.root.add(mesh);
    this.chunks.set(key, { mesh, center: mesh.boundingSphere!.center.clone(), size: LOOKS[kind].size, total, nearOnly: key.startsWith("near:") });
  }

  private async send(request: ScatterRequest): Promise<ScatterResult[]> {
    const id = ++this.requests;
    const worker = await this.scatterWorker();
    const direct = () => (this.direct ??= new ScatterComputer()).handle({ ...request, id });
    if (!worker) return direct();
    return new Promise((resolve) => {
      const onMessage = ({ data }: MessageEvent<ScatterResponse>) => {
        if (data.id !== id) return;
        worker.removeEventListener("message", onMessage);
        worker.removeEventListener("error", onError);
        resolve(data.results);
      };
      const onError = () => {
        worker.removeEventListener("message", onMessage);
        resolve(direct());
      };
      worker.addEventListener("message", onMessage);
      worker.addEventListener("error", onError);
      worker.postMessage({ ...request, id } satisfies ScatterRequest);
    });
  }

  private scatterWorker(): Promise<Worker | null> {
    if (typeof Worker === "undefined") return Promise.resolve(null);
    this.worker ??= import("./surfaceScatter.worker?worker")
      .then(({ default: ScatterWorker }) => {
        const worker = new ScatterWorker();
        worker.addEventListener("error", () => {
          worker.terminate();
          this.worker = Promise.resolve(null);
        });
        return worker;
      })
      .catch(() => null);
    return this.worker;
  }

  private geometryOf(kind: ScatterKind): THREE.BufferGeometry {
    let geometry = this.geometries.get(kind);
    if (!geometry) this.geometries.set(kind, (geometry = LOOKS[kind].geometry()));
    return geometry;
  }

  /** Sans éclairage de la scène, comme le sol que l'habillage prolonge. */
  private materialOf(kind: ScatterKind): THREE.Material {
    let material = this.materials.get(kind);
    if (material) return material;
    const look = LOOKS[kind];
    material = new THREE.MeshBasicMaterial({ side: look.side });
    if (look.grass) material.defines = { SCATTER_GRASS: "" };
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.sharedUniforms, { uSize: { value: look.size } });
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", `#include <common>\n${SCATTER_VERTEX_HEAD}`)
        .replace("#include <begin_vertex>", SCATTER_VERTEX_BODY);
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", `#include <common>\n${SCATTER_FRAGMENT_HEAD}`)
        .replace("#include <color_fragment>", SCATTER_FRAGMENT_COLOR);
    };
    this.materials.set(kind, material);
    return material;
  }
}
